import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useRefreshListener } from '../hooks/useRefreshListener';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Phone, FileText, CheckCircle2, AlertTriangle, Clock,
  Search as SearchIcon, Loader2, Trophy, Calendar as CalendarIcon, TrendingUp,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtDate = (iso) => { if (!iso) return '—'; try { return format(parseISO(iso), 'dd MMM yyyy', { locale: pt }); } catch { return iso; } };

const URGENCY = {
  overdue:    { label: 'Em atraso',  bg: 'bg-rose-50',    border: 'border-rose-300',    text: 'text-rose-800',    icon: AlertTriangle },
  today:      { label: 'Hoje',       bg: 'bg-amber-50',   border: 'border-amber-300',   text: 'text-amber-800',   icon: Clock },
  upcoming_7: { label: 'Próx 7 dias', bg: 'bg-blue-50',    border: 'border-blue-300',    text: 'text-blue-800',    icon: CalendarIcon },
  upcoming_30:{ label: 'Próx 30 dias', bg: 'bg-slate-50',   border: 'border-slate-300',   text: 'text-slate-700',   icon: CalendarIcon },
  future:     { label: 'Futuro',     bg: 'bg-slate-50',   border: 'border-slate-200',   text: 'text-slate-600',   icon: CalendarIcon },
};

const KPI_CARDS = [
  { key: 'overdue',     title: 'Em atraso',     icon: AlertTriangle, color: 'bg-rose-100 text-rose-700' },
  { key: 'today',       title: 'Hoje',          icon: Clock,         color: 'bg-amber-100 text-amber-700' },
  { key: 'upcoming_7',  title: 'Próximos 7d',   icon: CalendarIcon,  color: 'bg-blue-100 text-blue-700' },
  { key: 'upcoming_30', title: 'Próximos 30d',  icon: TrendingUp,    color: 'bg-slate-100 text-slate-700' },
];

const D45FollowUpsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [counters, setCounters] = useState({});
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState('upcoming_7'); // default: 7 dias
  const [search, setSearch] = useState('');

  const [completeOpen, setCompleteOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [target, setTarget] = useState(null);
  const [outcome, setOutcome] = useState('atendeu');
  const [method, setMethod] = useState('telefone');
  const [notes, setNotes] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (bucket && bucket !== 'all') params.append('bucket', bucket);
      if (search.trim()) params.append('search', search.trim());
      const res = await axios.get(`${API_URL}/leads/d45-followups?${params}`);
      setItems(res.data.items || []);
      setCounters(res.data.counters || {});
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar D+45 follow-ups');
    } finally {
      setLoading(false);
    }
  }, [bucket, search]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useRefreshListener(fetchData);

  const openComplete = (item) => {
    setTarget(item);
    setOutcome('atendeu');
    setMethod('telefone');
    setNotes('');
    setCompleteOpen(true);
  };

  const submitComplete = async () => {
    if (!target) return;
    setCompleting(true);
    try {
      await axios.post(`${API_URL}/leads/d45-followups/${target.event_id}/complete`, { notes, outcome, method });
      toast.success('✅ Check-in D+45 registado!', { duration: 4000 });
      setCompleteOpen(false);
      setTarget(null);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao marcar como cumprido');
    } finally {
      setCompleting(false);
    }
  };

  const gotoContract = (it) => {
    if (it.contract_category && it.contract_company && it.contract_id) {
      navigate(`/contracts/${encodeURIComponent(it.contract_category)}/${encodeURIComponent(it.contract_company)}/${it.contract_id}`);
    } else if (it.contract_id) {
      navigate(`/contracts/all?q=${encodeURIComponent(it.contract_codigo || it.contract_id)}`);
    }
  };

  const renderUrgencyChip = (urgency, days) => {
    const u = URGENCY[urgency] || URGENCY.future;
    const Icon = u.icon;
    let label = u.label;
    if (urgency === 'overdue') label = `${-days}d atraso`;
    else if (urgency === 'today') label = 'Hoje!';
    else label = `em ${days}d`;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${u.bg} ${u.border} ${u.text}`}>
        <Icon className="w-3 h-3" /> {label}
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-6 p-2 md:p-0">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Phone className="w-7 h-7 text-[#2D3E6F]" />
              Check-ins D+45
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-xl">
              Contactos obrigatórios 45 dias após activação do contrato. Cumpre o D+45 para fortalecer a relação com o cliente e reduzir risco de chargeback.
            </p>
          </div>
          {(user?.role === 'comercial' || user?.role === 'chefe_equipa') && (
            <div className="flex items-center gap-2 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-full px-3 py-1.5">
              <Trophy className="w-4 h-4 text-amber-700" />
              <span className="text-sm font-medium text-amber-900">Os teus check-ins</span>
            </div>
          )}
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {KPI_CARDS.map(kpi => {
            const Icon = kpi.icon;
            const value = counters[kpi.key] || 0;
            const active = bucket === kpi.key;
            return (
              <Card
                key={kpi.key}
                className={`cursor-pointer transition-all border-2 ${active ? 'border-[#2D3E6F] shadow-md' : 'border-transparent'}`}
                onClick={() => setBucket(kpi.key)}
                data-testid={`kpi-${kpi.key}`}
              >
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`p-2.5 rounded-xl ${kpi.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs text-slate-500 uppercase tracking-wide">{kpi.title}</div>
                    <div className="text-2xl font-bold text-slate-900 tabular-nums">{value}</div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Search + bucket pills */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Procurar cliente…"
              className="pl-9 rounded-full"
              data-testid="d45-search"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {['overdue', 'today', 'upcoming_7', 'upcoming_30', 'all'].map(b => (
              <Button
                key={b}
                variant={bucket === b ? 'default' : 'outline'}
                size="sm"
                onClick={() => setBucket(b)}
                className={`rounded-full text-xs ${bucket === b ? 'bg-[#2D3E6F] hover:bg-[#1a2a4a]' : ''}`}
                data-testid={`bucket-${b}`}
              >
                {b === 'overdue' && '🔴 Em atraso'}
                {b === 'today' && '⏰ Hoje'}
                {b === 'upcoming_7' && '📅 7 dias'}
                {b === 'upcoming_30' && '🗓 30 dias'}
                {b === 'all' && 'Todos'}
              </Button>
            ))}
          </div>
        </div>

        {/* Table */}
        <Card className="rounded-2xl overflow-hidden border-slate-200">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="min-w-[180px]">Cliente</TableHead>
                  <TableHead className="min-w-[120px]">Contrato</TableHead>
                  <TableHead className="min-w-[110px]">D+45</TableHead>
                  <TableHead className="min-w-[110px]">Urgência</TableHead>
                  <TableHead className="min-w-[160px]">Atribuído</TableHead>
                  <TableHead className="min-w-[200px] text-right">Acções</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline" /></TableCell></TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <div className="flex flex-col items-center gap-2">
                        <CheckCircle2 className="w-10 h-10 text-emerald-300" />
                        <p className="text-sm text-slate-500">Sem check-ins D+45 nesta categoria.</p>
                        <p className="text-xs text-slate-400">Os check-ins aparecem automaticamente quando converteres uma lead em contrato.</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map(it => (
                    <TableRow key={it.event_id} data-testid={`d45-row-${it.event_id}`} className="hover:bg-slate-50/50">
                      <TableCell>
                        <div className="font-medium text-slate-900">{it.client_name}</div>
                        {it.client_phone && (
                          <div className="text-xs text-slate-500 flex items-center gap-1.5">
                            <Phone className="w-3 h-3" />
                            <a href={`tel:${it.client_phone}`} className="hover:text-[#2D3E6F]">{it.client_phone}</a>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-slate-700">
                          {it.contract_codigo ? (
                            <span className="font-mono bg-slate-100 px-2 py-0.5 rounded text-xs">#{it.contract_codigo}</span>
                          ) : '—'}
                        </div>
                        <div className="text-xs text-slate-500">{it.contract_company || ''}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-slate-700">{fmtDate(it.scheduled_at)}</div>
                        {it.contract_data_ativacao && (
                          <div className="text-[10px] text-slate-400">Ativ: {fmtDate(it.contract_data_ativacao)}</div>
                        )}
                      </TableCell>
                      <TableCell>{renderUrgencyChip(it.urgency, it.days_diff)}</TableCell>
                      <TableCell>
                        <div className="text-xs text-slate-700">{it.assigned_to_name || '—'}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          {it.lead_id && (
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/leads/${it.lead_id}`)} data-testid={`d45-goto-lead-${it.event_id}`} title="Ver lead">
                              <FileText className="w-4 h-4" />
                            </Button>
                          )}
                          {it.contract_id && (
                            <Button variant="ghost" size="sm" onClick={() => gotoContract(it)} data-testid={`d45-goto-contract-${it.event_id}`} title="Ver contrato">
                              <FileText className="w-4 h-4 text-emerald-600" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            onClick={() => openComplete(it)}
                            className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
                            data-testid={`d45-complete-${it.event_id}`}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                            Cumprido
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Complete D+45 dialog */}
        <Dialog open={completeOpen} onOpenChange={(o) => { if (!o && !completing) { setCompleteOpen(false); setTarget(null); } }}>
          <DialogContent className="max-w-md" data-testid="d45-complete-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                Registar Check-in D+45
              </DialogTitle>
            </DialogHeader>
            {target && (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                  <div className="font-medium text-slate-900">{target.client_name}</div>
                  <div className="text-xs text-slate-500">
                    Contrato #{target.contract_codigo} · {target.contract_company}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-600">Resultado</Label>
                    <Select value={outcome} onValueChange={setOutcome}>
                      <SelectTrigger data-testid="d45-outcome"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="atendeu">Atendeu</SelectItem>
                        <SelectItem value="nao_atendeu">Não atendeu</SelectItem>
                        <SelectItem value="reagendar">Reagendar</SelectItem>
                        <SelectItem value="outro">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-600">Canal</Label>
                    <Select value={method} onValueChange={setMethod}>
                      <SelectTrigger data-testid="d45-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="telefone">Telefone</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="presencial">Presencial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-600">Notas do check-in</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Ex: Cliente satisfeito com primeira fatura, sem questões."
                    rows={3}
                    data-testid="d45-notes"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setCompleteOpen(false); setTarget(null); }} disabled={completing}>Cancelar</Button>
              <Button
                onClick={submitComplete}
                disabled={completing}
                className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                data-testid="d45-submit-complete"
              >
                {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Marcar como Cumprido
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default D45FollowUpsPage;
